import { provideApolloClient } from '@vue/apollo-composable'
import mutationCartCreateEmpty from '@shc/nuxt-shc-cart-layer/graphql/mutations/mutationCartCreateEmpty.graphql'
import mutationCartProductsAdd from '@/graphql/mutations/mutationCartProductsAdd.graphql'
import type { ProductBase } from '~/types/productBase'
import type { CartUserInputErrorModel, ProductPrice } from '~/graphql/types/graphql'
import { getItemsForMiniCart } from '~/utils/getItemsForMiniCart'

export interface ProductAdded {
    brand?: string
    categoryId?: number
    categoryName?: string
    id: number
    name: string
    offerId?: number
    parentCategoryId?: number
    parentCategoryName: string
    shopName?: string
    sku: string
    quantity: number
    parentSku?: string
    productPrice: ProductPrice
}
interface AddToCart {
    pageName: string
    productsAdd: ProductAdded[]
    quantity?: number
}

export function useCart() {
    const { $bus } = useNuxtApp()
    const { ERROR_CART } = useAppConfig()
    const { clients } = useApollo()
    const cookieCartId = useRuntimeConfig().public.cookieCartId
    const cookieCustomerToken = useRuntimeConfig().public.cookieCustomerToken
    const cartId = useCookie<string | null>(cookieCartId, {
        sameSite: 'lax',
        secure: true,
    })
    const customerToken = useCookie<string | null>(cookieCustomerToken, {
        sameSite: 'lax',
        secure: true,
    })
    let requestTryCounter = 3

    provideApolloClient(clients!.default)

    const getCartId = async (force = false) => {
        if (!cartId.value || force) {
            try {
                await clients!.default
                    .mutate({
                        mutation: mutationCartCreateEmpty,
                    })
                    .then(({ data }) => {
                        cartId.value = data.cartCreateEmpty
                    })
            } catch (e) {
                console.error(e)
            }
        }

        return cartId.value
    }

    const addToCart = async (product: ProductBase, { pageName, productsAdd, quantity = 1 }: AddToCart, parent) => {
        try {
            const productsToAdd = productsAdd?.map(({ parentSku, sku, quantity, offerId }) => {
                return {
                    parentSku,
                    productId: sku,
                    quantity,
                    offerId,
                }
            })

            const addTo = await clients!.default.mutate({
                mutation: mutationCartProductsAdd,
                variables: {
                    cartId: await getCartId(),
                    products: productsToAdd,
                },
            })

            const cartData = addTo.data.cartProductsAdd

            const isCartErrors = cartData?.userErrors?.some(
                (error: CartUserInputErrorModel) => error.code === ERROR_CART,
            )

            if (cartData?.userErrors?.length && !isCartErrors) {
                $bus.$emit('addToCartError', {
                    message: cartData.userErrors[0].message,
                })
            } else {
                const { cartItems, subtotal } = getItemsForMiniCart(cartData)

                $bus.$emit('addToCartSuccess', {
                    product,
                    parent,
                    pageName,
                    quantity,
                    cartItems,
                    subtotal,
                    groupedItems: productsAdd,
                })
            }
        } catch (error) {
            let retryAddToCart = false
            const errorsMessages = [
                'could not find a cart with id',
                'the current user cannot perform operations on cart',
                "the cart isn't active",
            ]

            errorsMessages.forEach((errorMessage) => {
                if (error.message.toLowerCase().includes(errorMessage.toLowerCase())) {
                    retryAddToCart = true
                }
            })

            if (retryAddToCart && requestTryCounter) {
                requestTryCounter--
                cartId.value = null
                customerToken.value = null

                console.error('Error in UseCart - retry:', error)
                await addToCart(product, { pageName, productsAdd })
            } else {
                $bus.$emit('addToCartError', {
                    message: error.message,
                })
                console.error('Error in UseCart:', error)
            }
            requestTryCounter = 3
        } finally {
            $bus.$emit('addToCartDone', product.sku)
        }
    }

    return {
        addToCart,
        getCartId,
    }
}
